import React, { useCallback } from 'react';
import { emptyObject } from 'ui-components/default-props/empty';

import { useModal } from '../../../hooks/use-modal';
import { useWallet } from '../../hooks/use-wallet';
import { useTrackWorkflow, WorkflowSteps } from '../../../tracking/use-workflow-tracking';
import { Workflows } from '../../../tracking/tracking-constants';
import { useIsAccountEmailVerifiedSellerSetting } from '../../../hooks/use-seller-setting';
import { VerifyEmailModal } from '../../../components/verify-email-modal';
import { WALLET_REGISTRATION } from '../../../constants/email-resend-source';
import { useGlobalModalLaunchContext } from '../../../contexts/modal-context/global-modal-context';
import { useOnce } from '../../../hooks/use-once';
import { PAYMENT_TAB } from '../../utilities/constants';
import { useFulfillmentPlannerContext } from '../../../fulfillment-planner/context';

/**
 * A button component that opens either the wallet registration or update modal
 * @param {object} props - Component props
 * @param {string} [props.walletId] - The ID of the wallet to register or update
 * @param {string} props.trackingId - The tracking ID for analytics
 * @param {object} [props.modalProps=emptyObject] - Additional props to pass to the modal
 * @param {(options: {openModal?: () => void, isWalletRegistered?: boolean, hasWalletUpdate?: boolean}) => React.ReactNode} props.children - Render prop function
 * @param {boolean} [props.shouldModalBeOpenAtStart=false] - Whether to open the modal automatically on mount
 * @param {string} [props.initialStepId=PAYMENT_TAB] - The initial step to show in the modal
 * @returns {React.ReactElement} The rendered component
 */
export const WalletRegistrationOrUpdateButton = React.memo(
  ({
    walletId: walletIdProp,
    trackingId,
    modalProps = emptyObject,
    children: renderButton,
    shouldModalBeOpenAtStart = false,
    initialStepId = PAYMENT_TAB,
  }) => {
    const { openWalletRegistration, openWalletUpdate } = useGlobalModalLaunchContext();

    const requireExactWallet = true;
    const wallet = useWallet(walletIdProp, requireExactWallet);
    const hasWallet = wallet !== emptyObject;
    const { isWalletRegistered, hasWalletUpdate, walletId } = wallet;

    const { close: closeUnverifiedModal, isOpen: isUnverifiedModalOpen, open: openUnverifiedModal } = useModal();

    const trackWalletRegistration = useTrackWorkflow(Workflows.WALLET_REGISTRATION_FLOW);
    const isAccountEmailVerified = useIsAccountEmailVerifiedSellerSetting();
    const { totalCost } = useFulfillmentPlannerContext() || {};

    const _onOpen = useCallback(() => {
      if (!isAccountEmailVerified) {
        openUnverifiedModal();
        return;
      }

      const targetModalProps = { walletId, ...modalProps, totalCost };
      if (isWalletRegistered) {
        openWalletUpdate(targetModalProps);
      } else {
        openWalletRegistration(targetModalProps, initialStepId);
      }
      trackWalletRegistration(WorkflowSteps.START, {
        source: trackingId,
        isUpdate: isWalletRegistered,
      });
    }, [
      isAccountEmailVerified,
      walletId,
      modalProps,
      totalCost,
      isWalletRegistered,
      trackWalletRegistration,
      trackingId,
      openUnverifiedModal,
      openWalletUpdate,
      openWalletRegistration,
      initialStepId,
    ]);

    // effects
    useOnce(() => {
      if (!shouldModalBeOpenAtStart) {
        return;
      }
      _onOpen();
    });

    // render
    if (!hasWallet || (isWalletRegistered && !hasWalletUpdate)) {
      return renderButton({
        isWalletRegistered,
        hasWalletUpdate,
      });
    }

    return (
      <>
        {renderButton({
          openModal: _onOpen,
          isWalletRegistered,
          hasWalletUpdate,
        })}
        {isUnverifiedModalOpen && <VerifyEmailModal closeUnverifiedModal={closeUnverifiedModal} source={WALLET_REGISTRATION} />}
      </>
    );
  },
);
